"use client";

import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useUserLocation } from "@/hooks/use-user-location";
import { locationOverlayIndices, mapBoundsConstants, mapDefaults, mapInteractionTimeouts } from "@/info/map-defaults";
import { useSearchPageMapData } from "@/providers/search";
import { Bounds } from "@/providers/search/stores/map-store";
import { Location, LocationType } from "@/types/filters";
import { cn } from "@/utils/cn";
import { debounce } from "@/utils/debounce";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { LocateFixed } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchAreaButton } from "./search/button";
export const baseClasses = "cursor-pointer font-medium whitespace-nowrap rounded-full px-3 py-1 transition-all duration-150 text-xs flex-shrink-0 flex items-center gap-1 border-1 active:scale-95";
export const selectedClasses = "bg-black/90 border-black/90 text-white shadow-md";
export const unselectedClasses = "bg-black/10 border-black/0 text-black hover:bg-black/70 hover:text-white active:bg-black/90";
interface LocationOverlayProps {
  isMapDataFetching: boolean;
}

// Use the Location interface from the locations store
interface LocationWithCoordinates {
  id: string;
  name: string;
  type: LocationType;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

// Simplified coordinate extraction using the locations store format
function getLocationCoordinates(location: LocationWithCoordinates) {
  if (location.coordinates?.latitude !== undefined && location.coordinates?.longitude !== undefined && !isNaN(location.coordinates.latitude) && !isNaN(location.coordinates.longitude)) {
    return {
      lat: location.coordinates.latitude,
      lng: location.coordinates.longitude
    };
  }
  return null;
}
const defLocations: Location[] = [];
export const LocationOverlay = function LocationOverlay({
  isMapDataFetching
}: LocationOverlayProps) {
  const map = useMap();
  const {
    filters,
    setFilters
  } = useUrlFilters();
  const {
    userLocation,
    requestLocation
  } = useUserLocation();

  // Get coordinate sync functions for immediate URL updates on location chip clicks
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(filters.selectedLocationId || null);
  const [isUserLocationActive, setIsUserLocationActive] = useState(false);
  const isInitialLoad = useRef(true);
  const shouldUpdateSelectedIndex = useRef(true);
  // Track the last searched bounds to determine if "Search this area" should show
  const lastSearchedBounds = useRef<Bounds | null>(null);
  // const shouldAutoCenter = useRef(true);

  // Only show overlay if there are actual location filters in the URL/filters
  const hasLocationFilters = filters.locations && filters.locations.length > 0;

  // Memoize expensive location filtering and deduplication using locations store
  const uniqueLocations = filters.locations || defLocations;

  // Helper function to check if bounds have changed significantly
  const haveBoundsChanged = useCallback((currentBounds: Bounds | null): boolean => {
    if (!currentBounds || !lastSearchedBounds.current) {
      return false;
    }
    const threshold = mapBoundsConstants.BOUNDS_CHANGE_THRESHOLD_DEGREES;
    const northDiff = Math.abs(currentBounds.north - lastSearchedBounds.current.north);
    const southDiff = Math.abs(currentBounds.south - lastSearchedBounds.current.south);
    const eastDiff = Math.abs(currentBounds.east - lastSearchedBounds.current.east);
    const westDiff = Math.abs(currentBounds.west - lastSearchedBounds.current.west);
    return northDiff > threshold || southDiff > threshold || eastDiff > threshold || westDiff > threshold;
  }, []);

  // Persist selected location when locations change
  // Get initial selected index based on bounds changes
  const updateSelectedIndex = useCallback(({
    fallbackLocationId
  }: {
    fallbackLocationId?: string;
  } = {}) => {
    if (!shouldUpdateSelectedIndex.current) return;

    // Get initial selected index based on bounds and location matching
    const getInitialSelectedIndex = () => {
      if (!map) return 0;
      const currentBounds = map.getBounds()?.toJSON() as Bounds | null;

      // Check if bounds have changed (haveBoundsChanged handles null checks internally)
      if (haveBoundsChanged(currentBounds)) {
        return -1; // Show "Search this area" button
      }

      // Bounds haven't changed or not initialized - match with location chips
      const mapCenter = map.getCenter();
      if (!mapCenter) return 0;
      let foundIndex = -1;
      const mapLat = mapCenter.lat();
      const mapLng = mapCenter.lng();

      // Find location chip that matches map center coordinates
      for (let i = 0; i < uniqueLocations.length; i++) {
        if (foundIndex >= 0) break;
        const location = uniqueLocations[i];
        const coordinates = getLocationCoordinates(location);
        if (coordinates) {
          // Check if map center matches chip coordinates (with tolerance)
          const latDiff = Math.abs(mapLat - coordinates.lat);
          const lngDiff = Math.abs(mapLng - coordinates.lng);

          // Use a tolerance of ~100 meters (approximately 0.001 degrees)
          if (latDiff < 0.001 && lngDiff < 0.001) {
            foundIndex = i;
          }
        }
      }

      // Return found location or default to first
      return foundIndex >= 0 ? foundIndex : locationOverlayIndices.DEFAULT;
    };
    const newIndex = getInitialSelectedIndex();
    setSelectedIndex(newIndex);
    if (newIndex >= 0) {
      setSelectedLocationId(uniqueLocations[newIndex]?.id || null);
    } else {
      setSelectedLocationId(fallbackLocationId || null);
    }
  }, [map, uniqueLocations, haveBoundsChanged]);

  // Track previous locations length to detect actual location changes
  const prevLocationsRef = useRef<string>(JSON.stringify(uniqueLocations.map(l => l.id)));

  // Persist selected location when locations change
  useEffect(() => {
    if (!uniqueLocations || uniqueLocations.length === 0) {
      return;
    }

    // Check if locations actually changed (not just filters)
    const currentLocationIds = JSON.stringify(uniqueLocations.map(l => l.id));
    const locationsChanged = prevLocationsRef.current !== currentLocationIds;

    // If locations haven't changed, don't update - this prevents re-rendering when other filters change
    if (!locationsChanged) {
      return;
    }
    prevLocationsRef.current = currentLocationIds;

    // If we have a previously selected location ID, try to find it in the new locations
    if (selectedLocationId) {
      // Handle custom search area selection
      if (selectedLocationId === "custom") {
        setSelectedIndex(locationOverlayIndices.SEARCH_AREA);
        return;
      }
      const foundIndex = uniqueLocations.findIndex(loc => loc.id === selectedLocationId);
      if (foundIndex !== -1 && foundIndex === selectedIndex) {
        // Location still exists at the same index, no need to update
        return;
      } else if (foundIndex !== -1) {
        // Location exists but at different index, update index only
        setSelectedIndex(foundIndex);
        return;
      }
    }

    // If previously selected location is no longer available, re-evaluate based on map state
    updateSelectedIndex({
      fallbackLocationId: "custom"
    });
  }, [updateSelectedIndex, selectedLocationId, uniqueLocations, selectedIndex]);
  const mode = useSearchPageMapData(state => state.mode);
  const setMode = useSearchPageMapData(state => state.setMode);
  const fetchMapData = useSearchPageMapData(state => state.fetchMapData);
  const fetchNearByProjects = useSearchPageMapData(state => state.fetchNearByProjects);
  const {
    orchestrateFetch
  } = useDataFetchOrchestrator({
    mode,
    fetchMapData,
    fetchNearByProjects
  });

  // Auto-center map on selected location - memoize the selected location
  const selectedLocation = useMemo(() => {
    if (hasLocationFilters && uniqueLocations && selectedIndex >= 0 && selectedIndex < uniqueLocations.length) {
      return uniqueLocations[selectedIndex];
    }
    return null;
  }, [hasLocationFilters, uniqueLocations, selectedIndex]);

  // Track if we've set initial bounds
  const hasSetInitialBounds = useRef(false);

  // Set lastSearchedBounds after the first data fetch completes
  useEffect(() => {
    if (!map || hasSetInitialBounds.current || isMapDataFetching) return;
    const currentBounds = map.getBounds()?.toJSON() as Bounds;
    if (currentBounds) {
      lastSearchedBounds.current = currentBounds;
      hasSetInitialBounds.current = true;
    }
  }, [map, isMapDataFetching]);

  // Watch for data fetch completion to hide "Search this area" button
  useEffect(() => {
    if (!map) return;
    // When fetching completes after clicking "Search this area"
    if (!isMapDataFetching && selectedLocationId === "custom" && selectedIndex === locationOverlayIndices.SEARCH_AREA) {
      const newBounds = map.getBounds()?.toJSON() as Bounds;
      if (newBounds) {
        lastSearchedBounds.current = newBounds;
        setSelectedIndex(locationOverlayIndices.HIDDEN);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapDataFetching, selectedLocationId, selectedIndex]);
  // Note: 'map' is intentionally omitted - Maps API objects are mutable refs and adding them causes unnecessary re-runs

  // center, zoom change
  const debouncedUpdateSelectedIndex = useMemo(() => debounce(() => {
    // Only update if not currently fetching data to prevent jumping during filter application
    if (!isMapDataFetching) {
      shouldUpdateSelectedIndex.current = true; // Re-enable updates BEFORE calling updateSelectedIndex
      updateSelectedIndex();
    }
  }, mapInteractionTimeouts.MAP_CHANGE_DEBOUNCE_MS), [updateSelectedIndex, isMapDataFetching]);
  useEffect(() => {
    if (!map) return;
    const centerChangedListener = map.addListener("center_changed", debouncedUpdateSelectedIndex);
    const zoomChangedListener = map.addListener("zoom_changed", debouncedUpdateSelectedIndex);
    const dragStartListener = map.addListener("dragstart", () => setIsUserLocationActive(false));
    return () => {
      if (centerChangedListener) centerChangedListener.remove();
      if (zoomChangedListener) zoomChangedListener.remove();
      if (dragStartListener) dragStartListener.remove();
    };
  }, [map, debouncedUpdateSelectedIndex]);
  useEffect(() => {
    // Don't auto-center on initial load if URL coordinates are already set
    if (isInitialLoad.current) {
      return;
    }
    if (selectedLocation && map) {
      const coordinates = getLocationCoordinates(selectedLocation);
      if (coordinates) {
        map.setCenter(coordinates);
      }
    }
  }, [selectedLocation, map]);
  const handleLocationClick = useCallback((index: number, location: LocationWithCoordinates) => {
    if (!map) return;

    // STEP 1: Immediate UI updates - select chip and show loader
    setSelectedIndex(index);
    setSelectedLocationId(location.id);
    setIsUserLocationActive(false);
    shouldUpdateSelectedIndex.current = false;

    // STEP 2: Do all heavy work asynchronously in the background
    setTimeout(() => {
      // Handle map panning/zooming
      const coordinates = getLocationCoordinates(location);
      if (!coordinates) {
        return;
      }

      // Set appropriate zoom based on location type
      const targetZoom = location.type === "project" ? mapDefaults.projectZoom - 1 // Use same as floating controls (16)
      : mapDefaults.zoom; // Use default zoom (14)

      // Set mode for UI consistency
      if (location.type === "project" && mode !== "project") setMode("project");else if (location.type !== "project" && mode === "project") setMode("default");

      // Move map to new location - use setCenter + setZoom for immediate update
      map.setCenter(coordinates);
      map.setZoom(targetZoom);

      // Wait for map to finish repositioning before getting bounds
      // Use idle event to ensure map has finished all animations and tile loading
      let hasHandledIdle = false;
      const idleListener = map.addListener("idle", () => {
        // Prevent multiple executions
        if (hasHandledIdle) return;
        hasHandledIdle = true;

        // Clean up listener
        if (idleListener) idleListener.remove();

        // Now get the correct bounds after map has repositioned
        const currentBounds = map.getBounds()?.toJSON() as Bounds;
        const currentZoom = map.getZoom();

        // Transform filters with ALL current filters to ensure proper filtering
        const {
          filtersForGraphQL,
          newFilters
        } = transformFilters({
          filters: {
            ...filters,
            selectedLocationId: location.id
          },
          // Include all current filters
          selectedLocationId: location.id,
          mapBounds: currentBounds,
          mapZoom: currentZoom,
          from: "nav-overlay"
        });

        // Update URL parameters
        setFilters(newFilters, {
          useReplaceState: true
        });

        // Save current bounds as last searched bounds
        lastSearchedBounds.current = currentBounds;

        // Fetch data
        orchestrateFetch({
          filters: newFilters,
          filtersForGraphQL,
          map
        });
      });
    }, 0);
  }, [map, filters, setFilters, orchestrateFetch, mode, setMode]);

  // Track if we need to center on location after it becomes available
  const pendingCenterRef = useRef(false);

  // Centers the map to the given location and triggers a search once the map is idle
  const centerAndSearch = useCallback((location: {
    lat: number;
    lng: number;
  }) => {
    if (!map) return;
    map.panTo(location);
    map.setZoom(15);
    let hasHandledIdle = false;
    const idleListener = map.addListener("idle", () => {
      if (hasHandledIdle) return;
      hasHandledIdle = true;
      if (idleListener) idleListener.remove();
      const currentBounds = map.getBounds()?.toJSON() as Bounds;
      const currentZoom = map.getZoom();
      const {
        filtersForGraphQL,
        newFilters
      } = transformFilters({
        filters,
        selectedLocationId: "custom",
        mapBounds: currentBounds,
        mapZoom: currentZoom,
        from: "search-area"
      });
      setFilters(newFilters, {
        useReplaceState: true
      });
      lastSearchedBounds.current = currentBounds;
      setIsUserLocationActive(true);
      orchestrateFetch({
        filters: newFilters,
        filtersForGraphQL,
        map
      });
    });
  }, [map, filters, setFilters, orchestrateFetch, setIsUserLocationActive]);
  const centerOnLocation = useCallback(async () => {
    if (!map) return;

    // Use cached user location for instant centering + search
    if (userLocation) {
      centerAndSearch(userLocation);
      return;
    }

    // No cached location - mark pending and request fresh location
    pendingCenterRef.current = true;
    try {
      await requestLocation();
    } catch {
      pendingCenterRef.current = false;
      // Silently fail - user may have denied permission
    }
  }, [map, userLocation, requestLocation, centerAndSearch]);

  // Effect to center map when userLocation becomes available after requestLocation
  useEffect(() => {
    if (pendingCenterRef.current && userLocation && map) {
      pendingCenterRef.current = false;
      centerAndSearch(userLocation);
    }
  }, [userLocation, map, centerAndSearch]);

  // Always show location list to display fixed controls like 'My Location'
  const showLocationScrollList = true;
  return <>
			{showLocationScrollList && <div className="absolute top-3 left-1/2 z-20 -translate-x-1/2 bg-white/40 backdrop-blur-md rounded-full shadow-lg p-1 max-w-[calc(100dvw-2rem)] overflow-hidden">
					<div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
						{/* Current Location Button */}
						<button type="button" onClick={centerOnLocation} className={cn(baseClasses, isUserLocationActive ? selectedClasses : unselectedClasses, "px-2")} aria-label="Center on my location" title="Center on my location">
							<LocateFixed size={14} />
						</button>

						{selectedIndex === locationOverlayIndices.SEARCH_AREA && <SearchAreaButton isLoading={isMapDataFetching} setSelectedIndex={setSelectedIndex} onSearchAreaClick={() => {
          setSelectedLocationId("custom"); // Set to "custom" to indicate custom search area
        }} />}

						{uniqueLocations.map((location, index) => {
          const isSelected = index === selectedIndex;
          const hasCoordinates = !!location.coordinates;
          return <button type="button" key={`${location.id}-${index}-${location.name}`} onClick={() => handleLocationClick(index, location)} disabled={isMapDataFetching || !hasCoordinates} className={cn(baseClasses, isSelected ? selectedClasses : unselectedClasses, !hasCoordinates ? "opacity-50 cursor-not-allowed" : "")} aria-label={`Select ${location.name} location${!hasCoordinates ? " (no coordinates available)" : ""}`} title={!hasCoordinates ? "Location coordinates not available" : undefined}>
									{isMapDataFetching && isSelected ? <>
											<div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
											<span>Searching...</span>
										</> : <span>{location.name}</span>}
								</button>;
        })}
					</div>
				</div>}
		</>;
};